import { useEffect, useMemo, useState } from "react";
import {
  listWorkflows,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  type WorkflowGraph,
} from "@/api/workflows";
import { useSessionUser } from "@/hooks/useSessionUser";
import CapabilityGate from "@/components/CapabilityGate";
import { Button } from "@/components/ui/button";

export default function WorkflowBuilder() {
  const [workflows, setWorkflows] = useState<WorkflowGraph[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<{ id: string; entry: string; nodes: string; allowedRoles: string }>({
    id: "",
    entry: "",
    nodes: "{}",
    allowedRoles: "owner, admin, agent_manager",
  });
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = useSessionUser();
  const canManageWorkflows = hasPermission("manage_workflows");

  const loadWorkflows = async () => {
    const data = await listWorkflows();
    setWorkflows(data);
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const handleSelect = async (workflowId: string) => {
    setError(null);
    setStatus(null);
    setIsNew(false);
    setSelectedId(workflowId);
    const workflow = await getWorkflow(workflowId);
    setForm({
      id: workflow.id,
      entry: workflow.entry,
      nodes: JSON.stringify(workflow.nodes, null, 2),
      allowedRoles: (workflow.allowed_roles ?? ["owner", "admin", "agent_manager"]).join(", "),
    });
  };

  const handleNew = () => {
    setError(null);
    setStatus(null);
    setIsNew(true);
    setSelectedId(null);
    setForm({
      id: "wf_",
      entry: "",
      nodes: JSON.stringify({ node1: { id: "node1", agent: "booking", task: "example", next: [] } }, null, 2),
      allowedRoles: "owner, admin, agent_manager",
    });
  };

  const handleSave = async () => {
    if (!canManageWorkflows) return;
    setError(null);
    setStatus(null);
    let nodesObject: WorkflowGraph["nodes"] = {};
    try {
      nodesObject = JSON.parse(form.nodes);
    } catch (err) {
      setError("Invalid nodes JSON");
      return;
    }
    const graph: WorkflowGraph = {
      id: form.id,
      entry: form.entry,
      nodes: nodesObject,
      allowed_roles: form.allowedRoles
        .split(",")
        .map((role) => role.trim())
        .filter(Boolean),
    };
    try {
      if (isNew || !selectedId) {
        await createWorkflow(graph);
        setStatus("Workflow created");
      } else {
        await updateWorkflow(graph);
        setStatus("Workflow updated");
      }
      await loadWorkflows();
      setIsNew(false);
      setSelectedId(graph.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!canManageWorkflows) return;
    await deleteWorkflow(selectedId);
    setStatus("Workflow deleted");
    setSelectedId(null);
    setIsNew(false);
    setForm({ id: "", entry: "", nodes: "{}", allowedRoles: "owner, admin, agent_manager" });
    await loadWorkflows();
  };

  const selectedWorkflow = useMemo(() => workflows.find((wf) => wf.id === selectedId), [selectedId, workflows]);
  const exampleNodesJson = `{"node1": {"id": "node1", "agent": "booking", "task": "example", "next": []}}`;

  const content = (
    <div className="aa-page-shell space-y-6">
      <div className="flex flex-wrap items-center justify-between">
        <div>
          <h1 className="aa-page-title">
            <span className="aa-gradient-text">Workflow Builder</span>
          </h1>
          <p className="text-sm text-muted-foreground">Create and edit autopilot workflow graphs.</p>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={handleNew} disabled={!canManageWorkflows}>
          New Workflow
        </Button>
      </div>
      {!canManageWorkflows && <p className="text-sm text-muted-foreground">Read-only access. Editing requires MANAGE_WORKFLOWS.</p>}
      {status && <p className="text-sm text-emerald-600 dark:text-emerald-300">{status}</p>}
      {error && <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>}
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="aa-glass-card rounded-2xl p-4">
          <h2 className="aa-page-title mb-3 text-lg font-semibold">Workflow List</h2>
          {workflows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No workflows available.</p>
          ) : (
            <ul className="space-y-2 max-h-[24rem] overflow-y-auto">
              {workflows.map((workflow) => (
                <li
                  key={workflow.id}
                  className={`cursor-pointer rounded-lg border p-3 ${selectedId === workflow.id ? "border-primary/70 bg-primary/10" : "border-border bg-background/40"}`}
                  onClick={() => handleSelect(workflow.id)}
                >
                  <div className="text-sm font-semibold text-foreground">{workflow.id}</div>
                  <div className="text-xs text-muted-foreground">Entry: {workflow.entry}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="aa-glass-card lg:col-span-2 space-y-4 rounded-2xl p-4">
          <h2 className="aa-page-title text-lg font-semibold">Workflow Editor</h2>
          <form className="space-y-3 text-sm text-foreground/80" onSubmit={(event) => event.preventDefault()}>
            <div>
              <label className="block text-xs uppercase text-muted-foreground mb-1">Workflow ID</label>
              <input
                type="text"
                value={form.id}
                onChange={(event) => setForm((prev) => ({ ...prev, id: event.target.value }))}
                className="w-full rounded border border-border bg-background/70 px-3 py-2"
                disabled={!isNew && Boolean(selectedId)}
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-muted-foreground mb-1">Entry Node</label>
              <input
                type="text"
                value={form.entry}
                onChange={(event) => setForm((prev) => ({ ...prev, entry: event.target.value }))}
                className="w-full rounded border border-border bg-background/70 px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-muted-foreground mb-1">Allowed roles</label>
              <input
                type="text"
                value={form.allowedRoles}
                onChange={(event) => setForm((prev) => ({ ...prev, allowedRoles: event.target.value }))}
                className="w-full rounded border border-border bg-background/70 px-3 py-2"
                disabled={!canManageWorkflows}
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-muted-foreground mb-1">Nodes JSON</label>
              <textarea
                value={form.nodes}
                onChange={(event) => setForm((prev) => ({ ...prev, nodes: event.target.value }))}
                className="h-48 w-full rounded border border-border bg-background/70 px-3 py-2 font-mono text-xs"
                readOnly={!canManageWorkflows}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Example: <code>{exampleNodesJson}</code>
              </p>
            </div>
            <div className="flex gap-3">
              <Button type="button" onClick={handleSave} disabled={!canManageWorkflows}>
                Save
              </Button>
              {!isNew && selectedId && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={!canManageWorkflows}>
                  Delete
                </Button>
              )}
            </div>
          </form>
          {selectedWorkflow && (
            <div className="rounded-xl border border-border/70 bg-background/40 p-3 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Visual Overview</h3>
              <p className="text-xs text-muted-foreground">
                Owner: {selectedWorkflow.owner_user_id || "system"} • Allowed:{" "}
                {(selectedWorkflow.allowed_roles ?? ["owner", "admin", "agent_manager"]).join(", ")}
              </p>
              <div className="flex flex-wrap gap-3">
                {Object.values(selectedWorkflow.nodes).map((node) => (
                  <div key={node.id} className="w-36 rounded border border-border bg-background/70 p-2 text-xs">
                    <div className="font-semibold text-foreground">{node.id}</div>
                    <div>{node.agent}</div>
                    <div className="text-muted-foreground">{node.task}</div>
                    {node.next.length > 0 && <div className="text-muted-foreground">→ {node.next.join(", ")}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );

  return (
    <CapabilityGate flag="canEditWorkflows">
      {content}
    </CapabilityGate>
  );
}
